import { useState, useEffect, useCallback } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useOrganizationStore } from '@/stores/organization-store'
import { llmApiKeyService } from '@/services/llm-api-key'
import type { LLMAPIKey, LLMProviderValue } from '@/schemas/llm-api-key'
import { LLM_PROVIDER_INFO } from '@/schemas/llm-api-key'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import { useAgentWizardStore } from '@/stores/agent-wizard-store'
import {
  Key,
  CheckCircle2,
  XCircle,
  Loader2,
  Plus,
  Shield,
} from 'lucide-react'

type KeySource = 'org_default' | 'saved' | 'new'

interface ApiKeySelectorProps {
  provider: LLMProviderValue | null
}

export function ApiKeySelector({ provider }: ApiKeySelectorProps) {
  const { t } = useTranslation('agents')
  const { control, setValue, watch, getValues } = useFormContext<AgentWizardData>()
  const { currentOrganization } = useOrganizationStore()
  const registerPendingApiKeySaver = useAgentWizardStore((s) => s.registerPendingApiKeySaver)

  const [keySource, setKeySource] = useState<KeySource>('org_default')
  const [savedKeys, setSavedKeys] = useState<LLMAPIKey[]>([])
  const [isLoadingKeys, setIsLoadingKeys] = useState(false)
  const [newApiKey, setNewApiKey] = useState('')
  const [saveNewKey, setSaveNewKey] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)

  const selectedApiKeyId = watch('api_key_id')

  // Load saved API keys when provider changes
  useEffect(() => {
    const loadSavedKeys = async () => {
      if (!currentOrganization?.id || !provider) {
        setSavedKeys([])
        return
      }

      setIsLoadingKeys(true)
      try {
        const response = await llmApiKeyService.listApiKeys(
          currentOrganization.id,
          provider
        )
        setSavedKeys(response.api_keys)

        // Check if there's a default key
        const defaultKey = response.api_keys.find((k) => k.is_default)
        if (defaultKey && keySource === 'org_default') {
          setValue('api_key_id', defaultKey.id)
        }
      } catch (error) {
        console.error('Failed to load API keys:', error)
        setSavedKeys([])
      } finally {
        setIsLoadingKeys(false)
      }
    }

    loadSavedKeys()
  }, [currentOrganization?.id, provider, setValue, keySource])

  // Reset state when provider changes
  useEffect(() => {
    setKeySource('org_default')
    setNewApiKey('')
    setSaveNewKey(false)
    setNewKeyName('')
    setTestResult(null)
    setValue('api_key_id', null)
  }, [provider, setValue])

  // Handle key source change
  const handleKeySourceChange = (source: KeySource) => {
    setKeySource(source)
    setTestResult(null)

    if (source === 'org_default') {
      const defaultKey = savedKeys.find((k) => k.is_default)
      setValue('api_key_id', defaultKey?.id ?? null)
    } else if (source === 'new') {
      setValue('api_key_id', null)
    }
  }

  // Test the API key
  const handleTestConnection = async () => {
    if (!currentOrganization?.id || !provider) return

    setIsTesting(true)
    setTestResult(null)

    try {
      let result

      if (keySource === 'new' && newApiKey) {
        // Test new key without saving
        result = await llmApiKeyService.testApiKey(currentOrganization.id, {
          provider,
          api_key: newApiKey,
        })
      } else if (selectedApiKeyId) {
        // Test saved key
        result = await llmApiKeyService.testSavedApiKey(
          currentOrganization.id,
          selectedApiKeyId
        )
      } else {
        setTestResult({
          success: false,
          message: t('wizard.apiKey.selectOrInsertFirst'),
        })
        setIsTesting(false)
        return
      }

      setTestResult({
        success: result.success,
        message: result.message,
      })

      // If testing new key and it succeeded and user wants to save it
      if (
        keySource === 'new' &&
        result.success &&
        saveNewKey &&
        newKeyName.trim()
      ) {
        const createResult = await llmApiKeyService.createApiKey(
          currentOrganization.id,
          {
            name: newKeyName.trim(),
            provider,
            api_key: newApiKey,
            is_default: savedKeys.length === 0,
          }
        )
        setValue('api_key_id', createResult.api_key.id)
        setSavedKeys((prev) => [...prev, createResult.api_key])
        setNewApiKey('')
        setNewKeyName('')
        setSaveNewKey(false)
        setKeySource('saved')
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : t('wizard.apiKey.connectionError'),
      })
    } finally {
      setIsTesting(false)
    }
  }

  // Save callback: saves the new API key if needed (called by wizard before advancing/submitting)
  const saveNewKeyIfNeeded = useCallback(async () => {
    if (keySource !== 'new' || !newApiKey || !currentOrganization?.id || !provider) return

    // Already saved (e.g., via test connection)
    const currentApiKeyId = getValues('api_key_id')
    if (currentApiKeyId) return

    const keyName = newKeyName.trim() || `${LLM_PROVIDER_INFO[provider]?.label ?? provider} Key`

    const result = await llmApiKeyService.createApiKey(currentOrganization.id, {
      name: keyName,
      provider,
      api_key: newApiKey,
      is_default: savedKeys.length === 0,
    })

    setValue('api_key_id', result.api_key.id)
    setSavedKeys((prev) => [...prev, result.api_key])
  }, [keySource, newApiKey, currentOrganization?.id, provider, newKeyName, savedKeys.length, setValue, getValues])

  // Register save callback in the wizard store
  useEffect(() => {
    registerPendingApiKeySaver(saveNewKeyIfNeeded)
    return () => registerPendingApiKeySaver(null)
  }, [saveNewKeyIfNeeded, registerPendingApiKeySaver])

  if (!provider) {
    return null
  }

  const providerInfo = LLM_PROVIDER_INFO[provider]
  const hasDefaultKey = savedKeys.some((k) => k.is_default)
  const hasSavedKeys = savedKeys.length > 0

  return (
    <div className="space-y-4">
      <Label className="text-gray-300 flex items-center gap-2">
        <Key className="h-4 w-4" />
        {t('wizard.apiKey.title')}
      </Label>

      {/* Key Source Selection */}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={keySource === 'org_default' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleKeySourceChange('org_default')}
          disabled={!hasDefaultKey && !isLoadingKeys}
          className={cn(
            'transition-all',
            keySource === 'org_default'
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-white/5 border-gray-700/50 text-gray-300 hover:bg-white/10'
          )}
        >
          <Shield className="h-4 w-4 mr-2" />
          {t('wizard.apiKey.useOrgDefault')}
        </Button>
        <Button
          type="button"
          variant={keySource === 'saved' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleKeySourceChange('saved')}
          disabled={!hasSavedKeys && !isLoadingKeys}
          className={cn(
            'transition-all',
            keySource === 'saved'
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-white/5 border-gray-700/50 text-gray-300 hover:bg-white/10'
          )}
        >
          <Key className="h-4 w-4 mr-2" />
          {t('wizard.apiKey.selectSaved')}
        </Button>
        <Button
          type="button"
          variant={keySource === 'new' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleKeySourceChange('new')}
          className={cn(
            'transition-all',
            keySource === 'new'
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-white/5 border-gray-700/50 text-gray-300 hover:bg-white/10'
          )}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('wizard.apiKey.insertNew')}
        </Button>
      </div>

      {/* Content based on key source */}
      <div className="p-4 rounded-xl bg-white/5 border border-gray-700/50 space-y-4">
        {isLoadingKeys ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-400">{t('wizard.apiKey.loadingKeys')}</span>
          </div>
        ) : keySource === 'org_default' ? (
          <>
            {hasDefaultKey ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-300">
                  {t('wizard.apiKey.orgDefaultDesc')}{' '}
                  {providerInfo.label}.
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Key className="h-4 w-4" />
                  <span>
                    {savedKeys.find((k) => k.is_default)?.name} (
                    {savedKeys.find((k) => k.is_default)?.key_preview})
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-yellow-400">
                {t('wizard.apiKey.noDefaultKey')} {providerInfo.label}.
                {t('wizard.apiKey.noDefaultKeyDesc')}
              </p>
            )}
          </>
        ) : keySource === 'saved' ? (
          <div className="space-y-3">
            <Controller
              name="api_key_id"
              control={control}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? undefined}
                >
                  <SelectTrigger
                    className={cn(
                      'bg-white/5 border-gray-700/50 text-white',
                      'focus:border-indigo-500 focus:ring-indigo-500/20'
                    )}
                  >
                    <SelectValue placeholder={t('wizard.apiKey.selectSavedKey')} />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    {savedKeys.map((key) => (
                      <SelectItem
                        key={key.id}
                        value={key.id}
                        className="text-gray-200 focus:bg-indigo-500/20 focus:text-white"
                      >
                        <span className="flex items-center gap-2">
                          {key.name}
                          <span className="text-gray-500">{key.key_preview}</span>
                          {key.is_default && (
                            <span className="text-xs bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded">
                              {t('wizard.apiKey.default')}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {savedKeys.length === 0 && (
              <p className="text-sm text-gray-400">
                {t('wizard.apiKey.noSavedKeys')} {providerInfo.label}.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_api_key" className="text-gray-400 text-sm">
                {providerInfo.label} API Key
              </Label>
              <Input
                id="new_api_key"
                type="password"
                value={newApiKey}
                onChange={(e) => setNewApiKey(e.target.value)}
                placeholder={providerInfo.placeholder}
                className={cn(
                  'bg-white/5 border-gray-700/50 text-white font-mono',
                  'focus:border-indigo-500 focus:ring-indigo-500/20'
                )}
              />
              <p className="text-xs text-gray-500">{providerInfo.helpText}</p>
            </div>

            {/* Save option */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="save_new_key"
                  checked={saveNewKey}
                  onCheckedChange={(checked) => setSaveNewKey(checked === true)}
                  className="border-gray-600 data-[state=checked]:bg-indigo-600"
                />
                <Label
                  htmlFor="save_new_key"
                  className="text-sm text-gray-300 cursor-pointer"
                >
                  {t('wizard.apiKey.saveForFuture')}
                </Label>
              </div>

              {saveNewKey && (
                <div className="space-y-2 pl-6">
                  <Label htmlFor="new_key_name" className="text-gray-400 text-sm">
                    {t('wizard.apiKey.apiKeyName')}
                  </Label>
                  <Input
                    id="new_key_name"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder={t('wizard.apiKey.apiKeyNamePlaceholder')}
                    className={cn(
                      'bg-white/5 border-gray-700/50 text-white',
                      'focus:border-indigo-500 focus:ring-indigo-500/20'
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Test Connection Button */}
        {(keySource !== 'org_default' || hasDefaultKey) && (
          <div className="flex items-center gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleTestConnection}
              disabled={
                isTesting ||
                (keySource === 'new' && !newApiKey) ||
                (keySource === 'saved' && !selectedApiKeyId)
              }
              className="bg-white/5 border-gray-700/50 text-gray-300 hover:bg-white/10"
            >
              {isTesting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('wizard.apiKey.testing')}
                </>
              ) : (
                t('wizard.apiKey.testConnection')
              )}
            </Button>

            {testResult && (
              <div
                className={cn(
                  'flex items-center gap-2 text-sm',
                  testResult.success ? 'text-green-400' : 'text-red-400'
                )}
              >
                {testResult.success ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
